import React, { useEffect, useState } from "react";
import { CADENZA_EXCHANGES } from "../data/cadenzaDemoData";
import "./styling/CadenzaDemo.css";

/**
 * CadenzaDemo — homepage marketing preview of the Cadenza MCP integration.
 *
 * Shown as a transcript because that is genuinely what using Cadenza looks
 * like: the engineer asks their own AI client a question, the client calls
 * Cadenza's tools against the project, and answers from the returned data. The
 * tool-call chips are the point — they show the reply is grounded in the
 * project's own records rather than generated prose.
 *
 * All exchanges are illustrative, not transcripts of real client projects.
 */

const prefersReducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Reveal order: question → each tool call in turn → answer. */
const CALL_DELAY = 260;
const ANSWER_GAP = 420;

const CadenzaDemo = () => {
  const [active, setActive] = useState(0);
  const [step, setStep] = useState(0);

  const exchange = CADENZA_EXCHANGES[active];
  const stepCount = exchange.calls.length + 2;

  // Replay the reveal whenever a different question is picked. With reduced
  // motion the whole exchange is shown at once instead.
  useEffect(() => {
    if (prefersReducedMotion) {
      setStep(stepCount);
      return undefined;
    }
    setStep(0);
    const timers = [];
    for (let i = 1; i <= stepCount; i += 1) {
      const delay = i === stepCount ? i * CALL_DELAY + ANSWER_GAP : i * CALL_DELAY;
      timers.push(setTimeout(() => setStep(i), delay));
    }
    return () => timers.forEach(clearTimeout);
  }, [active, stepCount]);

  return (
    <div className="cadenza-demo">
      <div className="cadenza-demo__bar">
        <span className="cadenza-demo__client">Your AI assistant</span>
        {/* Reflects the exchange on screen: the read questions need only
            mcp:read, while building a schedule needs the write scope. */}
        <span
          className={`cadenza-demo__scope${
            exchange.scope === "mcp:read" ? "" : " is-write"
          }`}
        >
          {exchange.scope}
        </span>
        <span className="cadenza-demo__conn">
          <span className="cadenza-demo__conn-dot" />
          Connected to your project
        </span>
      </div>

      <div className="cadenza-demo__prompts" role="group" aria-label="Example questions">
        {CADENZA_EXCHANGES.map((e, i) => (
          <button
            key={e.question}
            type="button"
            className={`cadenza-demo__prompt${i === active ? " is-active" : ""}`}
            onClick={() => setActive(i)}
            aria-pressed={i === active}
          >
            {e.chip}
          </button>
        ))}
      </div>

      <div className="cadenza-demo__thread">
        <div className={`cadenza-demo__turn${step >= 1 ? " is-shown" : ""}`}>
          <span className="cadenza-demo__who">Asked</span>
          <p className="cadenza-demo__question">{exchange.question}</p>
        </div>

        <div className="cadenza-demo__calls">
          {exchange.calls.map((call, i) => (
            <span
              key={call}
              className={`cadenza-demo__call${step >= i + 2 ? " is-shown" : ""}`}
            >
              <span className="cadenza-demo__call-tick" aria-hidden="true">
                ✓
              </span>
              {call}
            </span>
          ))}
        </div>

        <div className={`cadenza-demo__turn${step >= stepCount ? " is-shown" : ""}`}>
          <span className="cadenza-demo__who">Answered from your data</span>
          {/* Answers are authored as segments rather than markup so the figures
              pulled from the project can be emphasised without innerHTML. */}
          <p className="cadenza-demo__answer">
            {exchange.answer.map((part, i) =>
              typeof part === "string" ? (
                <React.Fragment key={i}>{part}</React.Fragment>
              ) : (
                <strong key={i}>{part.em}</strong>
              )
            )}
          </p>
          {exchange.evidence && (
            <div className="cadenza-demo__evidence">
              {exchange.evidence.map((s) => (
                <span key={s.label} className="cadenza-demo__stat">
                  <span className="cadenza-demo__stat-label">{s.label}</span>
                  <span
                    className={`cadenza-demo__stat-value${s.down ? " is-down" : ""}`}
                  >
                    {s.value}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <p className="cadenza-demo__note">
          Illustrative exchange. Cadenza stays within what your own account is
          permitted to do, and every call is recorded in an audit trail.
        </p>
      </div>
    </div>
  );
};

export default CadenzaDemo;
